import _ from 'lodash';
import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { RuleGroupTypeIC } from 'react-querybuilder';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Chip from '@mui/material/Chip';
import FormHelperText from '@mui/material/FormHelperText';
import Add from '@mui/icons-material/Add';
import { Button } from '@/ui';
import { affSubIDConfiguration, AffSubIdListEntry, CriteriaPaneProps, DomainAffiliateEntry, Option } from '../types';
import {
  CriteriaRows,
  CriteriaSection,
  EmptyHint,
  EntryCard,
  EntryField,
  EntryFields,
  FieldGrid,
  IncludeExcludeField,
  MultiSelect,
  SelectIncludeExclude,
  SelectSimple,
  SelectSimpleWithGroups,
  TextArea,
} from '../FormComponents';
import { createFieldUpdater } from '../helpers';
import { UserAttribute } from '../../UserAttribute';
import { UrlParameter } from '../../UrlParameter';
import { UserAgent } from '../../UserAgent';
import { DomainSortButton } from '../../components/DomainSortButton';
import { sortDomainValues } from '../../components/domainSort';
import { useUserAttributes } from '../../../../views/OfferBlocks/queries';
import { useOfferQuestions, useQuestionAnswers } from '../../../../views/Offers/queries';
import { useLandingPageGroupsDtos } from '../../../../views/LandingPageGroups/queries';
import { useLandingPagesDtos } from '../../../../views/LandingPages/queries';
import { EMAIL_DOMAIN_OPTIONS, useAffiliateNameOrTagOptions, useDomainByIdOrTagOptions } from '../useCriteriaOptions';
import { AFF_SUB_LOGIC_OPTIONS } from '../constants';
import { HELP } from '../criteriaHelp';
import {
  duplicateBanner,
  duplicateFieldMessage,
  duplicatesForEntry,
  findDuplicateDomains,
  markDomainsTakenElsewhere,
} from '../domainAffiliateDuplicates';
import { errorReader, hasValue, isIncludeExclude } from './shared';
import type { CriteriaSectionDef } from './types';

const nonPublisher = ({ isPublisher }: { isPublisher: boolean }) => !isPublisher;

/* ------------------------------------------------- User Attributes / URL Parameters / User Agent */

/**
 * Include/Exclude plus a rule builder. The builder needs the full width of the section, so
 * the mode selector sits above it rather than beside it the way IncludeExcludeField lays out.
 */
function RuleSection({
  title,
  help,
  mode,
  onModeChange,
  error,
  children,
}: {
  title: string;
  help: string;
  mode: string;
  onModeChange: (v: string) => void;
  error: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <CriteriaSection title={title} help={help}>
      <IncludeExcludeField label='Include/Exclude' mode={mode} onModeChange={onModeChange} />
      {isIncludeExclude(mode) && (
        <div>
          {children}
          {error && <FormHelperText error>{error}</FormHelperText>}
        </div>
      )}
    </CriteriaSection>
  );
}

function UserAttributesSection({ state, setState, errors }: CriteriaPaneProps) {
  const { first } = errorReader(errors);
  const { data: userAttributes } = useUserAttributes(true, (data: { name: string }[]) =>
    _.orderBy(data, ['name'], ['asc'])
  );

  return (
    <RuleSection
      title='User Attributes'
      help={HELP.userAttributes}
      mode={state.userAttributesIncludeExclude}
      onModeChange={createFieldUpdater(setState, 'userAttributesIncludeExclude')}
      error={first(['criteriaIncludeOrExcludeUserAttributes'])}
    >
      <UserAttribute
        input={{
          value: state.userAttributes,
          onChange: (value: RuleGroupTypeIC) => setState((s) => ({ ...s, userAttributes: value })),
        }}
        userAttributes={userAttributes || []}
      />
    </RuleSection>
  );
}

function UrlParametersSection({ state, setState, errors }: CriteriaPaneProps) {
  const { first } = errorReader(errors);

  return (
    <RuleSection
      title='URL Parameters'
      help={HELP.urlParameters}
      mode={state.urlParametersIncludeExclude}
      onModeChange={createFieldUpdater(setState, 'urlParametersIncludeExclude')}
      error={first(['criteriaIncludeOrExcludeUrlParameters'])}
    >
      <UrlParameter
        input={{
          value: state.urlParameterRules,
          onChange: (value: RuleGroupTypeIC) => setState((s) => ({ ...s, urlParameterRules: value })),
        }}
      />
    </RuleSection>
  );
}

/** ZET-5033: match on the request's User-Agent header, e.g. to exclude a "Read Aloud" browser extension. */
function UserAgentSection({ state, setState, errors }: CriteriaPaneProps) {
  const { first } = errorReader(errors);

  return (
    <RuleSection
      title='User Agent'
      help={HELP.userAgent}
      mode={state.userAgentIncludeExclude}
      onModeChange={createFieldUpdater(setState, 'userAgentIncludeExclude')}
      error={first(['criteriaIncludeOrExcludeUserAgent'])}
    >
      <UserAgent
        input={{
          value: state.userAgentRules,
          onChange: (value: RuleGroupTypeIC) => setState((s) => ({ ...s, userAgentRules: value })),
        }}
      />
    </RuleSection>
  );
}

/* --------------------------------------------------------------------------- Affiliate Names */

function AffiliateNamesSection({ state, setState, errors, markFieldTouched }: CriteriaPaneProps) {
  const { first } = errorReader(errors);
  const affiliatesOrTags = useAffiliateNameOrTagOptions({ includeNull: true });

  return (
    <CriteriaSection title='Affiliates'>
      <IncludeExcludeField
        label='Affiliate Names'
        help={HELP.affiliateNames}
        mode={state.affiliateNamesIncludeExclude}
        onModeChange={createFieldUpdater(setState, 'affiliateNamesIncludeExclude')}
        modeError={first(['criteriaIncludeOrExcludeAffNames'], ['affiliateNamesIncludeExclude'])}
      >
        <MultiSelect
          ariaLabel='Affiliates/Tags'
          value={state.affiliateNames}
          onChange={createFieldUpdater(setState, 'affiliateNames')}
          options={affiliatesOrTags}
          error={first(['criteriaAffNames'], ['affiliateNames'])}
          onBlur={() => markFieldTouched('affiliateNames')}
        />
      </IncludeExcludeField>
    </CriteriaSection>
  );
}

/* --------------------------------------------------------------------------- Aff SubID entries */

const EMPTY_AFF_SUB_CONFIG: affSubIDConfiguration = {
  includeExclude: '',
  affiliate: '',
  subIds: '',
  subIdList: [],
  andOrLogic: '',
};

const EMPTY_AFF_SUB_ENTRY: AffSubIdListEntry = { includeExclude: '', affiliate: '', subIds: [] };

const toSubIdText = (subIds: string | string[] | undefined | null) =>
  Array.isArray(subIds) ? subIds.join('\n') : subIds || '';

function AffSubIdSection({ state, setState, errors }: CriteriaPaneProps) {
  const { first } = errorReader(errors);
  const affiliatesOrTags = useAffiliateNameOrTagOptions({ includeNull: true });
  const config = state.affSubIDConfiguration;
  const entries = config?.subIdList || [];
  const logic = config?.andOrLogic || '';

  const entryError = (idx: number, field: keyof AffSubIdListEntry) =>
    first(['affSubIDConfiguration', 'subIdList', idx, field]);
  const hasAnySubIds = toSubIdText(config?.subIds as string | string[])
    .split('\n')
    .some((line) => line.trim().length > 0);
  const legacySubIdsError = hasAnySubIds ? undefined : first(['affSubIDConfiguration', 'subIds']);

  const updateConfig = (update: (cfg: affSubIDConfiguration) => affSubIDConfiguration) =>
    setState((s) => ({ ...s, affSubIDConfiguration: update(s.affSubIDConfiguration || EMPTY_AFF_SUB_CONFIG) }));

  /**
   * Patches one entry. Entry 0 is mirrored into the config's top-level includeExclude /
   * affiliate / subIds, which older readers of the configuration still look at.
   */
  const updateEntry = (
    idx: number,
    patch: Partial<AffSubIdListEntry>,
    mirror: Partial<Pick<affSubIDConfiguration, 'includeExclude' | 'affiliate' | 'subIds'>>
  ) =>
    updateConfig((cfg) => {
      const subIdList = [...(cfg.subIdList || [])];
      subIdList[idx] = { ...(subIdList[idx] || EMPTY_AFF_SUB_ENTRY), ...patch };
      return { ...cfg, subIdList, ...(idx === 0 ? mirror : {}) };
    });

  const removeEntry = (idx: number) =>
    updateConfig((cfg) => {
      const subIdList = (cfg.subIdList || []).filter((_, j) => j !== idx);
      if (idx !== 0) return { ...cfg, subIdList };
      const next = subIdList[0];
      return {
        ...cfg,
        subIdList,
        includeExclude: next?.includeExclude || '',
        affiliate: next?.affiliate || '',
        subIds: toSubIdText(next?.subIds),
      };
    });

  return (
    <CriteriaSection
      title='Aff SubID Configuration'
      help={HELP.affSubIds}
      actions={
        logic && (
          <Button
            color='primary'
            startIcon={<Add />}
            onClick={() =>
              updateConfig((cfg) => ({ ...cfg, subIdList: [...(cfg.subIdList || []), { ...EMPTY_AFF_SUB_ENTRY }] }))
            }
          >
            Add Entry
          </Button>
        )
      }
    >
      <FieldGrid min='20rem'>
        <SelectSimple
          label='AND/OR logic'
          value={logic}
          onChange={(v) =>
            updateConfig((cfg) => ({
              ...cfg,
              andOrLogic: v === 'AND' || v === 'OR' || v === 'ANY' ? v : '',
              subIdList: cfg.subIdList || [],
            }))
          }
          options={AFF_SUB_LOGIC_OPTIONS}
          placeholder='Select Logic'
          help={HELP.affSubLogic}
        />
      </FieldGrid>

      {logic && entries.length === 0 && (
        <>
          {entryError(0, 'includeExclude') ? (
            <FormHelperText error>{entryError(0, 'includeExclude')}</FormHelperText>
          ) : (
            <EmptyHint>No entries yet. Use Add Entry to target an affiliate's sub IDs.</EmptyHint>
          )}
        </>
      )}

      {logic &&
        entries.map((entry, idx) => (
          <EntryCard key={idx} title={`Entry ${idx + 1}`} onRemove={() => removeEntry(idx)}>
            <EntryFields>
              <EntryField size='narrow'>
                <SelectIncludeExclude
                  label='Include/Exclude'
                  value={entry.includeExclude || ''}
                  error={entryError(idx, 'includeExclude')}
                  onChange={(v) => updateEntry(idx, { includeExclude: v }, { includeExclude: v })}
                />
              </EntryField>
              <EntryField size='medium'>
                <SelectSimpleWithGroups
                  label='Affiliate'
                  value={entry.affiliate || ''}
                  error={entryError(idx, 'affiliate')}
                  onChange={(v) => updateEntry(idx, { affiliate: v || '' }, { affiliate: v || '' })}
                  options={affiliatesOrTags}
                  groupsOrder={['Affiliates', 'Tags']}
                />
              </EntryField>
              <EntryField size='wide'>
                <TextArea
                  label='Sub IDs'
                  help={HELP.subIds}
                  value={toSubIdText(entry.subIds)}
                  placeholder='One sub ID per line'
                  // Raw text while typing so Enter visibly adds a line; split into a list on blur.
                  onChange={(v) => updateEntry(idx, { subIds: v }, { subIds: v })}
                  onBlur={(v) =>
                    updateEntry(
                      idx,
                      {
                        subIds: v
                          .split('\n')
                          .map((line) => line.trim())
                          .filter((line) => line.length > 0),
                      },
                      { subIds: v }
                    )
                  }
                  error={entryError(idx, 'subIds') || (idx === 0 ? legacySubIdsError : undefined)}
                />
              </EntryField>
            </EntryFields>
          </EntryCard>
        ))}
    </CriteriaSection>
  );
}

/* ------------------------------------------------------------------- Domain-specific Affiliates */

function DomainAffiliatesSection({ state, setState, errors, markFieldTouched }: CriteriaPaneProps) {
  const { first } = errorReader(errors);
  const { options: domainsOrTags, direction, toggleDirection } = useDomainByIdOrTagOptions();
  const affiliatesOrTags = useAffiliateNameOrTagOptions({ includeNull: true });
  const storedEntries = state.domainAffiliates?.domainAffiliateEntries;
  const entries = storedEntries || [];

  const setEntries = (update: (list: DomainAffiliateEntry[]) => DomainAffiliateEntry[]) =>
    setState((s) => ({
      ...s,
      domainAffiliates: { domainAffiliateEntries: update(s.domainAffiliates?.domainAffiliateEntries || []) },
    }));
  const updateEntry = (idx: number, patch: Partial<DomainAffiliateEntry>) =>
    setEntries((list) => list.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  const errorFor = (idx: number, field: keyof DomainAffiliateEntry) =>
    first(
      ['domainAffiliateConfiguration', 'domainAffiliateEntries', idx, field],
      ['domainAffiliates', 'domainAffiliateEntries', idx, field]
    );

  // ZET-4737: a domain may appear in one entry only. Flagged here as you type (the validator
  // repeats it at save), and a domain another entry holds is greyed out in this entry's list.
  const duplicateDomains = useMemo(() => findDuplicateDomains(storedEntries), [storedEntries]);
  const banner = useMemo(() => duplicateBanner(duplicateDomains, domainsOrTags), [duplicateDomains, domainsOrTags]);

  return (
    <CriteriaSection
      title='Domain-specific Affiliates'
      help={HELP.domainAffiliates}
      actions={
        <>
          <DomainSortButton direction={direction} onToggle={toggleDirection} />
          <Button
            color='primary'
            startIcon={<Add />}
            onClick={() =>
              setEntries((list) => [...list, { domains: [], includeExcludeAction: 'Include', affiliates: [] }])
            }
          >
            Add Entry
          </Button>
        </>
      }
    >
      {entries.length === 0 && (
        <EmptyHint>No entries yet. Use Add Entry to limit affiliates on specific domains.</EmptyHint>
      )}
      {banner && (
        <Alert severity='error' role='alert'>
          <AlertTitle>{banner.title}</AlertTitle>
          {banner.body}
        </Alert>
      )}
      {entries.map((entry, idx) => {
        const active = isIncludeExclude(entry.includeExcludeAction);
        const hasDuplicateDomain = duplicatesForEntry(duplicateDomains, idx).length > 0;
        return (
          <EntryCard
            key={idx}
            title={`Entry ${idx + 1}`}
            error={hasDuplicateDomain}
            actions={
              hasDuplicateDomain && <Chip size='small' color='error' variant='outlined' label='Duplicate domain' />
            }
            onRemove={() => setEntries((list) => list.filter((_, j) => j !== idx))}
          >
            <EntryFields>
              <EntryField size='narrow'>
                <SelectIncludeExclude
                  label='Include/Exclude'
                  value={entry.includeExcludeAction ?? 'Include'}
                  onChange={(v) => updateEntry(idx, { includeExcludeAction: v })}
                  error={errorFor(idx, 'includeExcludeAction')}
                />
              </EntryField>
              {active && (
                <>
                  <EntryField size='wide'>
                    {/* ZET-4463: picked chips follow the dropdown's DomainID order, tags first. */}
                    <MultiSelect
                      label='Domains/Tags'
                      value={sortDomainValues(entry.domains, domainsOrTags)}
                      onChange={(v) => updateEntry(idx, { domains: sortDomainValues(v, domainsOrTags) })}
                      options={markDomainsTakenElsewhere(domainsOrTags, entries, idx)}
                      error={duplicateFieldMessage(duplicateDomains, idx) ?? errorFor(idx, 'domains')}
                      onBlur={() => markFieldTouched(`domainAffiliates.domainAffiliateEntries.${idx}.domains`)}
                    />
                  </EntryField>
                  <EntryField size='wide'>
                    <MultiSelect
                      label='Affiliates/Tags'
                      value={entry.affiliates ?? []}
                      onChange={(v) => updateEntry(idx, { affiliates: v })}
                      options={affiliatesOrTags}
                      error={errorFor(idx, 'affiliates')}
                      onBlur={() => markFieldTouched(`domainAffiliates.domainAffiliateEntries.${idx}.affiliates`)}
                    />
                  </EntryField>
                </>
              )}
            </EntryFields>
          </EntryCard>
        );
      })}
    </CriteriaSection>
  );
}

/* ----------------------------------------------------------------------------- Email Domains */

function EmailDomainsSection({ state, setState, errors, markFieldTouched }: CriteriaPaneProps) {
  const { first } = errorReader(errors);

  return (
    <CriteriaSection title='Email Domains'>
      <IncludeExcludeField
        label='Email Domains'
        help={HELP.emailDomains}
        mode={state.emailDomainsIncludeExclude}
        onModeChange={createFieldUpdater(setState, 'emailDomainsIncludeExclude')}
        modeError={first(['criteriaIncludeOrExcludeEmailDomains'], ['emailDomainsIncludeExclude'])}
      >
        <MultiSelect
          ariaLabel='Domains'
          value={state.emailDomains}
          onChange={createFieldUpdater(setState, 'emailDomains')}
          options={EMAIL_DOMAIN_OPTIONS}
          error={first(['criteriaEmailDomains'], ['emailDomains'])}
          onBlur={() => markFieldTouched('emailDomains')}
        />
      </IncludeExcludeField>
    </CriteriaSection>
  );
}

/* ----------------------------------------------------------------------------- Landing Pages */

function LandingPagesSection({ state, setState, errors, markFieldTouched }: CriteriaPaneProps) {
  const { first } = errorReader(errors);
  const { data: landingPageGroups } = useLandingPageGroupsDtos();
  const { data: landingPages } = useLandingPagesDtos();

  const groupOptions = useMemo<Option[]>(
    () => landingPageGroups?.map((group) => ({ label: group.name, value: String(group.id) })) || [],
    [landingPageGroups]
  );
  const pageOptions = useMemo<Option[]>(
    () => landingPages?.map((page) => ({ label: page.offer?.name || 'Unnamed Offer', value: String(page.id) })) || [],
    [landingPages]
  );

  // Older configurations stored names; anything that resolves to a known record is saved by ID.
  const toGroupIds = (values: string[]) =>
    values.map((val) => {
      const group = landingPageGroups?.find((g) => g.name === val || String(g.id) === val);
      return group ? String(group.id) : val;
    });
  const toPageIds = (values: string[]) =>
    values.map((val) => {
      const page = landingPages?.find((p) => String(p.id) === val || p.offer?.name === val);
      return page ? String(page.id) : val;
    });

  return (
    <CriteriaSection title='Landing Pages'>
      <CriteriaRows>
        <IncludeExcludeField
          label='Landing Page Groups'
          help={HELP.landingPageGroups}
          mode={state.landingPageGroupIncludeExclude}
          onModeChange={createFieldUpdater(setState, 'landingPageGroupIncludeExclude')}
          modeError={first(['criteriaLandingPageGroupIncludeExclude'], ['landingPageGroupIncludeExclude'])}
        >
          <MultiSelect
            ariaLabel='Groups'
            value={state.landingPageGroupIds}
            onChange={(v) => setState((s) => ({ ...s, landingPageGroupIds: toGroupIds(v) }))}
            options={groupOptions}
            error={first(['criteriaLandingPageGroupIds'], ['landingPageGroupIds'])}
            onBlur={() => markFieldTouched('landingPageGroupIds')}
          />
        </IncludeExcludeField>
        <IncludeExcludeField
          label='Landing Pages'
          help={HELP.landingPages}
          mode={state.landingPageIncludeExclude}
          onModeChange={createFieldUpdater(setState, 'landingPageIncludeExclude')}
          modeError={first(['criteriaLandingPageIncludeExclude'], ['landingPageIncludeExclude'])}
        >
          <MultiSelect
            ariaLabel='Pages'
            value={state.landingPageIds}
            onChange={(v) => setState((s) => ({ ...s, landingPageIds: toPageIds(v) }))}
            options={pageOptions}
            error={first(['criteriaLandingPageIds'], ['landingPageIds'])}
            onBlur={() => markFieldTouched('landingPageIds')}
          />
        </IncludeExcludeField>
      </CriteriaRows>
    </CriteriaSection>
  );
}

/* ------------------------------------------------------------------------- Criteria Question */

type OfferQuestion = { id: number; name: string; question: string };
type QuestionAnswerRecord = string | { answer?: string | null };

function CriteriaQuestionSection({ state, setState, sourceType, errors, markFieldTouched }: CriteriaPaneProps) {
  const { first } = errorReader(errors);
  const { data: questions } = useOfferQuestions({ enabled: sourceType === 'offer' }) as UseQueryResult<OfferQuestion[]>;
  const questionOptions = useMemo<Option[]>(
    () => questions?.map((q) => ({ label: `${q.name} - ${q.question}`, value: q.question })) || [],
    [questions]
  );
  const selectedQuestion = questions?.find((q) => q.question === state.question);
  const { data: answers } = useQuestionAnswers(selectedQuestion?.id) as UseQueryResult<QuestionAnswerRecord[]>;
  const answerOptions: Option[] = (answers ?? []).flatMap((answer) => {
    const value = typeof answer === 'string' ? answer : (answer?.answer ?? '');
    return value ? [{ label: value, value }] : [];
  });

  return (
    <CriteriaSection title='Criteria Question' help={HELP.criteriaQuestion}>
      <FieldGrid min='18rem'>
        <SelectSimple
          label='Question'
          value={state.question}
          // Answers belong to the question, so a new question starts with none.
          onChange={(v) => setState((s) => ({ ...s, question: v, questionAnswers: [] }))}
          options={questionOptions}
          placeholder='Select Question'
          error={first(['criteriaQuestion'], ['question'])}
        />
        {state.question && (
          <MultiSelect
            label='Answers'
            value={state.questionAnswers}
            onChange={createFieldUpdater(setState, 'questionAnswers')}
            options={answerOptions}
            error={first(['criteriaQuestionAnswers'], ['questionAnswers'])}
            help={HELP.criteriaQuestionAnswers}
            onBlur={() => markFieldTouched('questionAnswers')}
          />
        )}
      </FieldGrid>
    </CriteriaSection>
  );
}

/* ---------------------------------------------------------------------------------- Registry */

export const ATTRIBUTE_SECTIONS: CriteriaSectionDef[] = [
  {
    id: 'userAttributes',
    tab: 'attribute',
    title: 'User Attributes',
    fields: ['Include/Exclude'],
    aliases: ['attributes', 'user attributes', 'rules'],
    stateKeys: ['userAttributesIncludeExclude', 'userAttributes'],
    isConfigured: (s) => isIncludeExclude(s.userAttributesIncludeExclude),
    Component: UserAttributesSection,
  },
  {
    id: 'urlParameters',
    tab: 'attribute',
    title: 'URL Parameters',
    fields: ['Include/Exclude'],
    aliases: ['url', 'query string', 'params', 'parameters', 'utm'],
    stateKeys: ['urlParametersIncludeExclude', 'urlParameterRules'],
    isConfigured: (s) => isIncludeExclude(s.urlParametersIncludeExclude),
    Component: UrlParametersSection,
  },
  {
    id: 'userAgent',
    tab: 'attribute',
    title: 'User Agent',
    fields: ['Include/Exclude'],
    aliases: ['ua', 'user agent', 'browser', 'bot', 'crawler', 'read aloud', 'header'],
    stateKeys: ['userAgentIncludeExclude', 'userAgentRules'],
    isConfigured: (s) => isIncludeExclude(s.userAgentIncludeExclude),
    Component: UserAgentSection,
  },
  {
    id: 'affiliateNames',
    tab: 'attribute',
    title: 'Affiliates',
    fields: ['Affiliate Names'],
    aliases: ['affiliates', 'null affiliate', 'tags'],
    stateKeys: ['affiliateNamesIncludeExclude', 'affiliateNames'],
    appliesTo: nonPublisher,
    isConfigured: (s) => isIncludeExclude(s.affiliateNamesIncludeExclude),
    Component: AffiliateNamesSection,
  },
  {
    id: 'affSubIds',
    tab: 'attribute',
    title: 'Aff SubID Configuration',
    fields: ['AND/OR logic'],
    aliases: ['Sub IDs', 'Affiliate', 'aff_sub', 'sub id', 'subid'],
    stateKeys: ['affSubIDConfiguration'],
    appliesTo: nonPublisher,
    isConfigured: (s) => Boolean(s.affSubIDConfiguration?.andOrLogic) && hasValue(s.affSubIDConfiguration?.subIdList),
    Component: AffSubIdSection,
  },
  {
    id: 'domainAffiliates',
    tab: 'attribute',
    title: 'Domain-specific Affiliates',
    fields: ['DomainID'],
    aliases: ['Domains/Tags', 'Affiliates/Tags', 'domain', 'affiliate'],
    stateKeys: ['domainAffiliates'],
    appliesTo: nonPublisher,
    isConfigured: (s) => hasValue(s.domainAffiliates?.domainAffiliateEntries),
    Component: DomainAffiliatesSection,
  },
  {
    id: 'emailDomains',
    tab: 'attribute',
    title: 'Email Domains',
    fields: ['Email Domains'],
    aliases: ['gmail', 'yahoo', 'email'],
    stateKeys: ['emailDomainsIncludeExclude', 'emailDomains'],
    isConfigured: (s) => isIncludeExclude(s.emailDomainsIncludeExclude),
    Component: EmailDomainsSection,
  },
  {
    id: 'landingPages',
    tab: 'attribute',
    title: 'Landing Pages',
    fields: ['Landing Page Groups', 'Landing Pages'],
    aliases: ['lp', 'lpg', 'landing'],
    stateKeys: ['landingPageGroupIncludeExclude', 'landingPageGroupIds', 'landingPageIncludeExclude', 'landingPageIds'],
    isConfigured: (s) =>
      isIncludeExclude(s.landingPageGroupIncludeExclude) || isIncludeExclude(s.landingPageIncludeExclude),
    Component: LandingPagesSection,
  },
  {
    id: 'criteriaQuestion',
    tab: 'attribute',
    title: 'Criteria Question',
    fields: ['Question'],
    aliases: ['Answers', 'question', 'answer'],
    stateKeys: ['question', 'questionAnswers'],
    appliesTo: ({ sourceType }) => sourceType !== 'offerWallAd',
    isConfigured: (s) => hasValue(s.question),
    Component: CriteriaQuestionSection,
  },
];
